import _ from "lodash";
import React, { memo, useState, useEffect } from "react";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";
import { getAssignmentMessageObj } from "./Utils";
import { formatDate, scrollAndHighlightBackground, isNumber } from "Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { colors } from "@toddle-edu/ds-theme";
import { Button } from "@toddle-edu/ds-web";
import AITutorChatPreviewModal from "../AITutorChatPreviewModal";

import classes from "./AssignmentActivity.scss";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { connect } from "react-redux";
import { getAIConversationsFlaggedMessagesFromCache } from "AppComponents/AITutorFlaggedMessages/modules/AITutorFlaggedMessagesGraphqlHelpers";
import { updateEvaluationData } from "ClassRoom/modules/ClassRoomModule";
import { getIsStaff } from "modules/CommonSelectors";
import { FlagFilled } from "@toddle-edu/ds-icons";

const INITIAL_DIALOG = { action: "", params: {} };

const AssignmentActivity = props => {
  const {
    message,
    isStaff,
    studentData,
    plannerElementSets,
    getCurrentAssessmentTypes,
    highlightMessageId,
    updateEvaluationData,
  } = props;

  const [currentDialog, setCurrentDialog] = useState(INITIAL_DIALOG);

  const { t } = useI18n();

  // case for AI tutor where we need to highlight the message in the activity log
  useEffect(() => {
    if (isNumber(highlightMessageId)) {
      const element = scrollAndHighlightBackground({
        id: highlightMessageId,
        className: classes.subtleBackground,
        block: "center",
        inline: "center",
        timeout: 2000,
      });

      if (element) updateEvaluationData({ highlightMessageId: null });
    }
  }, [highlightMessageId, message]);

  const {
    activeUserId: currentUserId,
    currentUserType,
    curriculumProgramType,
  } = useMessageOptions();

  const {
    icon,
    label,
    iconBgColor = colors.surfaceSubtle,
    bgColor = colors.surfaceWhite,
    fontColor = colors.textSubtle,
  } = getAssignmentMessageObj({
    t,
    currentUserId,
    currentUserType,
    message,
    studentData,
    plannerElementSets,
    curriculumProgramType,
    getCurrentAssessmentTypes,
  });

  const formattedDate = formatDate({
    date: message?.createdAt,
    format: DATE_TIME_CONSTANTS.SENT_MESSAGE,
  });

  const { createdAt, metadata } = message || {};
  const { entityId, aiConversationId } = metadata || {};

  const renderMessageAction = () => {
    switch (message?.itemTypeV2) {
      case "STUDENT_ASSIGNMENT_RESTART_AI_CONVERSATION_SESSION": {
        if (!aiConversationId) return null;

        const { aiConversations } = getAIConversationsFlaggedMessagesFromCache({
          id: entityId,
        });

        const flaggedMessagesCount = _.get(
          _.find(
            aiConversations?.edges,
            ({ node }) => node?.id === aiConversationId
          ),
          "node.messages.totalCount",
          0
        );

        const onClickSeePreviousConversation = () => {
          setCurrentDialog({
            action: "AI_TUTOR_CHAT_MODAL",
            params: {
              aiConversationId,
              conversationCreatedAt: createdAt,
              entityId,
            },
          });
        };

        return (
          <div className={classes.prevConversationContainer}>
            <Button
              dsVersion={"2.0"}
              variant={"progressive"}
              type={"inline"}
              size={"small"}
              onClick={onClickSeePreviousConversation}
            >
              {t("ai:see_previous_conversation")}
            </Button>

            {isStaff && flaggedMessagesCount > 0 ? (
              <>
                <div className={classes.dot} />
                <div className={classes.flaggedCountContainer}>
                  <FlagFilled size={"xxxx-small"} variant={"critical"} />
                  <span
                    className={classNames("text-body-s", classes.flaggedCount)}
                  >
                    {flaggedMessagesCount}
                  </span>
                </div>
              </>
            ) : null}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const onCloseDialog = () => setCurrentDialog(INITIAL_DIALOG);

  const renderDialog = () => {
    const { action, params } = currentDialog || {};

    switch (action) {
      case "AI_TUTOR_CHAT_MODAL": {
        return (
          <AITutorChatPreviewModal
            source={"ACTIVITY_LOG"}
            onClose={onCloseDialog}
            {...(params || {})}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <div
        id={aiConversationId ?? ""}
        className={classes.container}
        style={{ backgroundColor: bgColor }}
      >
        <div className={classes.icon} style={{ backgroundColor: iconBgColor }}>
          {icon}
        </div>

        <div className={classes.labelContainer}>
          <div
            className={classNames("text-body-2", classes.label)}
            style={{ color: fontColor }}
          >
            {label}

            <span className={classes.separator}>•</span>
            <span className={classNames("text-body-3", classes.date)}>
              {formattedDate}
            </span>
          </div>

          {renderMessageAction()}
        </div>
      </div>

      {renderDialog()}
    </>
  );
};

const mapStateToProps = state => {
  const isStaff = getIsStaff(state);

  const highlightMessageId = _.get(
    state,
    "classRoom.evaluation.highlightMessageId",
    null
  );

  return { isStaff, highlightMessageId };
};

const mapActionCreators = {
  getCurrentAssessmentTypes,
  updateEvaluationData,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(memo(AssignmentActivity));
